/**
 * Verify API client.
 * In production verify.bootcamp-institutex.com proxies /api/v1/* to the HTTP API Gateway
 * via CloudFront, so the frontend just calls relative paths.
 */
export interface OpenBadgesAssertion {
  '@context': string;
  type: string;
  id: string;
  recipient: {
    type: 'email';
    identity: string;        // sha256$<hash>
    hashed: true;
    salt: string;
  };
  badge: string;             // URL to BadgeClass JSON-LD
  verification: { type: 'HostedBadge' };
  issuedOn: string;
  image?: string;
  narrative?: string;
  evidence?: Array<{ id?: string; narrative?: string; name?: string }>;
  revoked?: boolean;
  revocationReason?: string;
  expires?: string;
  /** Bootcamp Institute extension — full display name (when consent allows) */
  recipientName?: string;
  /** @deprecated alias for recipientName, retained for backward compatibility */
  recipientRedacted?: string;
  /** Bootcamp Institute extension — denormalized skills array from BadgeClass (migration 0008) */
  skills?: string[];
}

export interface CriteriaLayer {
  layer: number;
  name: string;
  criterion: string;
  threshold: string;
}

export interface OpenBadgesBadgeClass {
  '@context': string;
  type: 'BadgeClass';
  id: string;
  name: string;
  description: string;
  image: string;
  criteria: {
    id?: string;
    narrative?: string;
    /** Bootcamp Institute extension — structured criteria from curriculum */
    layers?: CriteriaLayer[];
  };
  issuer: string;
  alignment?: Array<{ targetName: string; targetUrl?: string; targetFramework?: string }>;
  tags?: string[];
  /** Bootcamp Institute extension — credential validity period in years */
  validityYears?: number;
}

export interface OpenBadgesIssuer {
  '@context': string;
  type: 'Issuer';
  id: string;
  name: string;
  url: string;
  email: string;
  image?: string;
  description?: string;
  publicKey?: Array<{ id: string; publicKeyPem: string }>;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export async function fetchAssertion(code: string): Promise<OpenBadgesAssertion | { status: number }> {
  const res = await fetch(`${API_BASE}/api/v1/verify/${encodeURIComponent(code)}`, {
    headers: { Accept: 'application/ld+json, application/json' },
  });
  if (!res.ok) return { status: res.status };
  return await res.json();
}

export async function fetchBadgeClass(badgeUrl: string): Promise<OpenBadgesBadgeClass | { status: number }> {
  const res = await fetch(badgeUrl, { headers: { Accept: 'application/ld+json' } });
  if (!res.ok) return { status: res.status };
  return await res.json();
}

export async function fetchIssuer(issuerUrl: string): Promise<OpenBadgesIssuer | { status: number }> {
  const res = await fetch(issuerUrl, { headers: { Accept: 'application/ld+json' } });
  if (!res.ok) return { status: res.status };
  return await res.json();
}
